import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import ArrowDropUp from '@mui/icons-material/ArrowDropUp';
import {
  Badge,
  BadgeProps,
  Collapse,
  Grid,
  InputBase,
  LinearProgress,
  List,
  ListItem,
} from '@mui/material';
import debounce from 'lodash.debounce';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { UNDEFINED_DISPLAY_KEY } from '../../../../constants';
import { GlobalFilterTypeHandle } from '../../../../context/types';
import { BetterSuspense } from '../../BetterSuspense';
import { LoadingCircle } from '../../LoadingCircle';
import { Filter } from '../filterbar/types';
import { ContextLocation, FilterSection, HierarchyItem } from '../types';
import { FilterTraySection } from './FilterTraySection';

const StyledFilterInput = styled(InputBase)`
  font-size: 0.8125rem;
  width: 100%;

  textarea {
    padding: 2px 20px 2px 0px;
    color: ${(props) => props.theme.color.secondary};

    &::placeholder {
      color: ${(props) => props.theme.color.secondary}
      font-size: 0.8125rem;
      opacity: 1;
    }

    &:focus::placeholder {
      opacity: 0.5;
    }
  }
`;

const StyledBadge = styled(Badge)<BadgeProps>(() => ({
  flexShrink: 'unset !important',
  '& .MuiBadge-badge': {
    top: 14,
    fontWeight: '800',
    color: 'white',
    backgroundColor: 'transparent',
    border: `1px solid white`,
    padding: '0px 4px',
  },
}));

export interface FilterTraySectionWrapperProps<FS extends FilterSection> {
  backgroundColor: string;
  section: FS;
  filterHandle: GlobalFilterTypeHandle<Filter>;
  searchResult?: HierarchyItem[] | null;
}

export const FilterTraySectionWrapper = <FS extends FilterSection>(
  props: FilterTraySectionWrapperProps<FS>
) => {
  const { t } = useTranslation();
  const { section, backgroundColor, filterHandle, searchResult } = props;
  const [subItems, setSubItems] = useState<HierarchyItem[] | null>(null);
  const hierarchyRef = useRef<HierarchyItem[] | null>(null);
  const [searchText, setSearchText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>();
  const [isOpen, setIsOpen] = useState(false);
  const [hasRendered, setHasRendered] = useState(
    !!searchText || Boolean(searchResult)
  );
  const [filterState] = filterHandle;

  const handleSearchClick = useCallback(
    (e: React.MouseEvent) => (isOpen || !!searchText) && e.stopPropagation(),
    [isOpen, !!searchText]
  );

  const selectedFiltersCount = useMemo(
    () =>
      filterState.items.filter(
        (i) =>
          section.dataField.dataType === i.dataType &&
          section.dataField.dataField === i.property
      ).length,
    [filterState.items, section.dataField]
  );

  const searchTextChanged = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    const result = await section.searchFn(e.target.value, hierarchyRef.current);
    setSubItems(result);
  };

  const handleListClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      setIsOpen((prev) => !prev);
      setHasRendered(true);
      inputRef?.current?.focus();
    },
    [isOpen]
  );

  return (
    <List sx={{ padding: '0px !important', backgroundColor }} dense>
      <ListItem
        key={`filter-tray-section-wrapper-list-${section.key.hash}`}
        dense
        onClick={handleListClick}
        className="intercom_filter"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '2px 4px 2px 16px',
        }}
      >
        <Grid item>
          {/*TODO: bring back the filter number when the CBP have discussed whether we want it or not
          If we keep it we have to make it reactive to the timeslider range */}
          {/*<StyledBadge badgeContent={selectedFiltersCount} color="primary">*/}
          <StyledFilterInput
            id={`filter-tray-section-wrapper-input-${section.key.hash}-${0}`}
            inputRef={inputRef}
            autoComplete="off"
            placeholder={section.labelFn(t, t(UNDEFINED_DISPLAY_KEY), {
              location: ContextLocation.FILTERTRAY,
            })}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setIsLoading(true);
              void debounce(async (e1: React.ChangeEvent<HTMLInputElement>) => {
                await searchTextChanged(e1);
                setIsLoading(false);
              }, 1000)(e);
            }}
            onClick={handleSearchClick}
            inputProps={{
              'aria-label': 'naked',
            }}
            multiline
          />
          {isLoading ? <LoadingCircle /> : null}
          {/*</StyledBadge>*/}
        </Grid>
        <Grid item>
          {section.isExpandable &&
            (isOpen || !!searchText || Boolean(searchResult) ? (
              <ArrowDropUp
                color="secondary"
                sx={{ cursor: 'pointer' }}
                data-cy="panalyt-filter-expand-less"
              />
            ) : (
              <ArrowDropDown
                color="secondary"
                sx={{ cursor: 'pointer' }}
                data-cy="panalyt-filter-expand-more"
              />
            ))}
        </Grid>
      </ListItem>
      <Collapse
        in={isOpen || !!searchText || Boolean(searchResult)}
        timeout="auto"
      >
        {hasRendered ? (
          <BetterSuspense
            key={`filter-tray-section-${section.key.hash}`}
            fallback={<LinearProgress />}
          >
            <FilterTraySection
              subItems={searchResult ?? subItems}
              hierarchyRef={hierarchyRef}
              filterHandle={filterHandle}
              filterDataManager={section.filterDataManagerForFilterTray}
              forceOpen={!!searchText || Boolean(searchResult)}
            />
          </BetterSuspense>
        ) : null}
      </Collapse>
    </List>
  );
};
